import { DependencyList, useCallback, useState, useRef } from 'react';
import { useMountedState } from 'react-use';
import { EXCLUDE_ERROR_CODES } from 'src/constants';
import { IErrorData } from 'src/interface';
import { AsyncUtils, Logger } from 'src/utils';

export type AsyncState<T> =
    | {
        loading: boolean;
        error?: undefined | IErrorData;
        value?: undefined;
    }
    | {
        loading: false;
        error: IErrorData;
        value?: undefined;
    }
    | {
        loading: false;
        error?: undefined | IErrorData;
        value: T;
    };

export type AsyncFn<Result = any, Args extends any[] = any[]> = [
    AsyncState<Result>,
    (...args: Args | any) => any
];

export function useAsyncFn<Result = any, Args extends any[] = any[]>(
    fn: (...args: Args) => any,
    deps: DependencyList = [],
    initialState: AsyncState<Result> = { loading: false }
): AsyncFn<Result, Args> {
    const lastCallId = useRef(0);
    const [state, set] = useState<AsyncState<Result>>(initialState);
    const isMounted = useMountedState();

    const callback = useCallback((...args: Args) => {
        const callId = ++lastCallId.current;
        set({ loading: true });
        return AsyncUtils.asyncAction(() => {
            return fn(...args);
        }).then(
            (value) => {
                if (isMounted() && callId === lastCallId.current) {
                    set({ value, loading: false });
                }
                return value;
            },
            (error) => {
                if (isMounted() && callId === lastCallId.current) {
                    if (
                        error &&
                        error.err &&
                        EXCLUDE_ERROR_CODES.indexOf(error.err) <= -1
                    ) {
                        Logger.error(error.err, fn.name, 'Error', error);
                    }

                    set({ error, loading: false });
                }
                return error;
            }
        );
    }, deps);

    return [state, callback];
}

export default useAsyncFn;
